import React, { useState, useMemo } from 'react';
import { Card, CardContent } from './ui/card';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Calculator, TrendingUp, Euro, Calendar, Percent, PieChart } from 'lucide-react';

/**
 * Mortgage / Loan Calculator
 * Calculates monthly payment, total interest, total paid based on principal, rate, and term
 */
export const MortgageCalculator = ({ lang = 'sq', initialPrice = 250000 }) => {
  const [price, setPrice] = useState(initialPrice);
  const [downPayment, setDownPayment] = useState(50000);
  const [rate, setRate] = useState(4.5); // annual interest %
  const [term, setTerm] = useState(20); // years

  const labels = lang === 'sq' ? {
    title: 'Kalkulatori i Kredisë',
    subtitle: 'Llogarit pagesën mujore për pronën tuaj',
    price: 'Çmimi i Pronës',
    downPayment: 'Pagesa Fillestare',
    rate: 'Norma vjetore e interesit',
    term: 'Kohëzgjatja (vite)',
    loan: 'Shuma e Kredisë',
    monthly: 'Pagesa Mujore',
    totalInterest: 'Interesi Total',
    totalPaid: 'Total i Paguar',
    breakdown: 'Ndarja Financiare',
    downPercentage: '% e Pagesës Fillestare',
    yearsShort: 'vjet',
  } : {
    title: 'Mortgage Calculator',
    subtitle: 'Calculate your monthly payment for the property',
    price: 'Property Price',
    downPayment: 'Down Payment',
    rate: 'Annual interest rate',
    term: 'Term (years)',
    loan: 'Loan Amount',
    monthly: 'Monthly Payment',
    totalInterest: 'Total Interest',
    totalPaid: 'Total Paid',
    breakdown: 'Financial Breakdown',
    downPercentage: '% Down Payment',
    yearsShort: 'yrs',
  };

  const calculations = useMemo(() => {
    const loan = Math.max(0, price - downPayment);
    const monthlyRate = rate / 100 / 12;
    const numPayments = term * 12;

    let monthly = 0;
    if (loan > 0 && monthlyRate > 0 && numPayments > 0) {
      monthly = (loan * monthlyRate * Math.pow(1 + monthlyRate, numPayments)) /
                (Math.pow(1 + monthlyRate, numPayments) - 1);
    } else if (loan > 0 && numPayments > 0) {
      monthly = loan / numPayments;
    }
    const totalPaid = monthly * numPayments;
    const totalInterest = totalPaid - loan;
    const downPct = price > 0 ? (downPayment / price) * 100 : 0;

    return { loan, monthly, totalPaid, totalInterest, downPct };
  }, [price, downPayment, rate, term]);

  const fmt = (n) => new Intl.NumberFormat('sq-AL', { maximumFractionDigits: 0 }).format(Math.round(n || 0));

  return (
    <section id="calculator" className="py-32 bg-gradient-to-b from-black via-slate-950 to-black relative overflow-hidden">
      {/* Decorative background */}
      <div className="absolute inset-0 opacity-30">
        <div className="absolute top-1/3 left-0 w-96 h-96 bg-[#C5A05E]/15 rounded-full blur-3xl" />
        <div className="absolute bottom-1/3 right-0 w-96 h-96 bg-[#D4B06E]/10 rounded-full blur-3xl" />
      </div>

      <div className="relative z-10 max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Header */}
        <div className="text-center mb-14 space-y-6">
          <div className="inline-flex items-center gap-3 px-6 py-2 bg-[#C5A05E]/10 backdrop-blur-sm border border-[#C5A05E]/30 rounded-full text-[#C5A05E] text-sm font-semibold uppercase tracking-wider animate-fadeInUp">
            <Calculator className="w-4 h-4" />
            {labels.title}
          </div>
          <h2 className="text-5xl md:text-6xl font-bold text-white animate-fadeInUp" style={{ animationDelay: '0.1s' }}>
            {labels.subtitle}
          </h2>
          <div className="flex items-center justify-center gap-3 animate-fadeInUp" style={{ animationDelay: '0.2s' }}>
            <div className="h-px w-24 bg-gradient-to-r from-transparent to-[#C5A05E]" />
            <div className="w-2 h-2 bg-[#C5A05E] rounded-full" />
            <div className="h-px w-24 bg-gradient-to-l from-transparent to-[#C5A05E]" />
          </div>
        </div>

        <div className="grid lg:grid-cols-5 gap-8">
          {/* Inputs Card */}
          <div className="lg:col-span-2 animate-fadeInUp" style={{ animationDelay: '0.3s', opacity: 0, animationFillMode: 'forwards' }}>
            <div className="relative">
              <div className="absolute -inset-1 bg-gradient-to-br from-[#C5A05E]/30 to-transparent rounded-3xl blur-xl" />
              <Card className="relative backdrop-blur-2xl bg-white/5 border border-white/10">
                <CardContent className="p-8 space-y-6">
                  <div>
                    <label className="text-sm font-semibold text-white mb-3 flex items-center gap-2">
                      <Euro className="w-4 h-4 text-[#C5A05E]" />
                      {labels.price}
                    </label>
                    <Input
                      type="number"
                      value={price}
                      onChange={(e) => setPrice(parseFloat(e.target.value) || 0)}
                      className="bg-white/5 border-white/20 text-white text-2xl font-bold focus:border-[#C5A05E] py-7 rounded-xl"
                      data-testid="mortgage-price"
                    />
                  </div>

                  <div>
                    <label className="text-sm font-semibold text-white mb-3 flex items-center gap-2">
                      <PieChart className="w-4 h-4 text-[#C5A05E]" />
                      {labels.downPayment}
                      <span className="ml-auto text-[#C5A05E] text-xs font-normal">
                        {calculations.downPct.toFixed(1)}% {labels.downPercentage}
                      </span>
                    </label>
                    <Input
                      type="number"
                      value={downPayment}
                      onChange={(e) => setDownPayment(parseFloat(e.target.value) || 0)}
                      className="bg-white/5 border-white/20 text-white text-xl font-semibold focus:border-[#C5A05E] py-6 rounded-xl"
                      data-testid="mortgage-down"
                    />
                    {/* Slider */}
                    <input
                      type="range"
                      min={0}
                      max={price}
                      step={1000}
                      value={downPayment}
                      onChange={(e) => setDownPayment(parseFloat(e.target.value))}
                      className="w-full mt-3 accent-[#C5A05E]"
                    />
                  </div>

                  <div>
                    <label className="text-sm font-semibold text-white mb-3 flex items-center gap-2">
                      <Percent className="w-4 h-4 text-[#C5A05E]" />
                      {labels.rate}
                      <span className="ml-auto text-[#C5A05E] text-lg font-bold">{rate}%</span>
                    </label>
                    <input
                      type="range"
                      min={1}
                      max={12}
                      step={0.1}
                      value={rate}
                      onChange={(e) => setRate(parseFloat(e.target.value))}
                      className="w-full accent-[#C5A05E]"
                      data-testid="mortgage-rate"
                    />
                  </div>

                  <div>
                    <label className="text-sm font-semibold text-white mb-3 flex items-center gap-2">
                      <Calendar className="w-4 h-4 text-[#C5A05E]" />
                      {labels.term}
                      <span className="ml-auto text-[#C5A05E] text-lg font-bold">{term} {labels.yearsShort}</span>
                    </label>
                    <input
                      type="range"
                      min={5}
                      max={30}
                      step={1}
                      value={term}
                      onChange={(e) => setTerm(parseInt(e.target.value))}
                      className="w-full accent-[#C5A05E]"
                      data-testid="mortgage-term"
                    />
                  </div>
                </CardContent>
              </Card>
            </div>
          </div>

          {/* Results Card */}
          <div className="lg:col-span-3 animate-fadeInUp" style={{ animationDelay: '0.5s', opacity: 0, animationFillMode: 'forwards' }}>
            <div className="relative h-full">
              <div className="absolute -inset-1 bg-gradient-to-br from-[#D4B06E]/40 via-[#C5A05E]/30 to-[#B39050]/40 rounded-3xl blur-2xl" />
              
              <Card className="relative backdrop-blur-2xl bg-gradient-to-br from-[#C5A05E]/10 via-black/60 to-black/80 border border-[#C5A05E]/30 h-full">
                <CardContent className="p-8 space-y-6">
                  {/* Monthly Payment - Big Display */}
                  <div className="text-center py-4">
                    <p className="text-sm text-gray-400 uppercase tracking-wider mb-3">{labels.monthly}</p>
                    <div className="relative inline-block">
                      <div className="absolute inset-0 bg-[#C5A05E]/30 blur-3xl" />
                      <div className="relative text-6xl md:text-7xl font-bold bg-gradient-to-br from-[#E8C77E] via-[#D4B06E] to-[#C5A05E] bg-clip-text text-transparent" data-testid="mortgage-monthly">
                        €{fmt(calculations.monthly)}
                      </div>
                    </div>
                    <p className="text-gray-500 mt-2 text-sm">
                      × {term * 12} {lang === 'sq' ? 'muaj' : 'months'}
                    </p>
                  </div>

                  <div className="h-px bg-gradient-to-r from-transparent via-[#C5A05E]/30 to-transparent" />

                  {/* Breakdown Grid */}
                  <div>
                    <h3 className="text-lg font-bold text-white mb-4 flex items-center gap-2">
                      <TrendingUp className="w-5 h-5 text-[#C5A05E]" />
                      {labels.breakdown}
                    </h3>
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                      <div className="p-4 backdrop-blur-xl bg-white/5 rounded-2xl border border-white/10 hover:border-[#C5A05E]/50 transition-colors">
                        <p className="text-xs text-gray-400 uppercase tracking-wider mb-2">{labels.loan}</p>
                        <p className="text-2xl font-bold text-white" data-testid="mortgage-loan">€{fmt(calculations.loan)}</p>
                      </div>
                      <div className="p-4 backdrop-blur-xl bg-white/5 rounded-2xl border border-white/10 hover:border-[#C5A05E]/50 transition-colors">
                        <p className="text-xs text-gray-400 uppercase tracking-wider mb-2">{labels.totalInterest}</p>
                        <p className="text-2xl font-bold text-[#C5A05E]" data-testid="mortgage-interest">€{fmt(calculations.totalInterest)}</p>
                      </div>
                      <div className="p-4 backdrop-blur-xl bg-white/5 rounded-2xl border border-white/10 hover:border-[#C5A05E]/50 transition-colors">
                        <p className="text-xs text-gray-400 uppercase tracking-wider mb-2">{labels.totalPaid}</p>
                        <p className="text-2xl font-bold text-white" data-testid="mortgage-total">€{fmt(calculations.totalPaid)}</p>
                      </div>
                    </div>
                  </div>

                  {/* Payment breakdown chart (visual) */}
                  <div className="mt-4">
                    <div className="flex items-center gap-2 mb-2 text-xs text-gray-400">
                      <span className="flex items-center gap-1"><div className="w-3 h-3 rounded bg-[#C5A05E]"></div>{lang === 'sq' ? 'Principal' : 'Principal'}</span>
                      <span className="flex items-center gap-1"><div className="w-3 h-3 rounded bg-[#5B7FB5]"></div>{labels.totalInterest}</span>
                    </div>
                    <div className="h-4 rounded-full overflow-hidden flex bg-white/5 border border-white/10">
                      <div
                        className="bg-gradient-to-r from-[#C5A05E] to-[#D4B06E] h-full transition-all duration-500"
                        style={{ width: `${(calculations.loan / (calculations.totalPaid || 1)) * 100}%` }}
                      />
                      <div
                        className="bg-gradient-to-r from-[#5B7FB5] to-[#4A6A9E] h-full transition-all duration-500"
                        style={{ width: `${(calculations.totalInterest / (calculations.totalPaid || 1)) * 100}%` }}
                      />
                    </div>
                  </div>

                  <p className="text-xs text-gray-500 text-center pt-4">
                    {lang === 'sq' 
                      ? '* Ky është një kalkulim orientues. Kontaktoni bankën për oferta reale.'
                      : '* This is an indicative calculation. Contact your bank for real offers.'}
                  </p>
                </CardContent>
              </Card>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};
